import { matchPath } from 'react-router-dom';
import * as ROUTES from '../constants/routes.js';

/**
 * Detect project sub-page
 * For URLs like /{slug}/projects/{projectId}/[page]
 */
export const getProjectPage = (pathname) => {
  const match = matchPath(ROUTES.PROJECTS_PAGE_PATTERN, pathname);
  if (match) {
    return match.params.page;
  }

  return null;
};

/**
 * Detect if on projects list with filter
 * For URLs like /{slug}/projects/[filter]
 */
export const getProjectsFilter = (pathname) => {
  const match = matchPath(ROUTES.PROJECTS_FILTER_PATTERN, pathname);
  if (match && match.params.filter) {
    // Make sure it's not a numeric projectId
    if (isNaN(match.params.filter)) {
      return match.params.filter;
    }
  }

  return null;
};

/**
 * Detect change impact page
 * For URLs like /{slug}/projects/{projectId}/change-impacts/[action or id]
 */
export const getChangeImpactPage = (pathname) => {
  if (matchPath(ROUTES.CHANGE_IMPACTS_NEW, pathname)) {
    return 'new';
  }

  if (matchPath(ROUTES.CHANGE_IMPACTS_DATA_IMPORT, pathname)) {
    return 'data-import';
  }

  if (matchPath(ROUTES.CHANGE_IMPACTS_EDIT, pathname)) {
    return 'edit';
  }

  if (matchPath(ROUTES.CHANGE_IMPACTS_SHOW, pathname)) {
    return 'show';
  }

  return null;
};

/**
 * Detect task page
 */
export const getTaskPage = (pathname) => {
  if (matchPath(ROUTES.TASKS_NEW, pathname)) {
    return 'new';
  }

  if (matchPath(ROUTES.TASKS_EDIT, pathname)) {
    return 'edit';
  }

  if (matchPath(ROUTES.TASKS_SHOW, pathname)) {
    return 'show';
  }

  return null;
};

/**
 * Detect report type
 * For URLs like /{slug}/project-reports or /{slug}/change-impact-reports
 */
export const getReportType = (pathname) => {
  if (matchPath(ROUTES.PROJECT_REPORTS, pathname)) {
    return 'project-reports';
  }

  if (matchPath(ROUTES.CHANGE_IMPACT_REPORTS, pathname)) {
    return 'change-impact-reports';
  }

  return null;
};

/**
 * Detect initiative page type
 */
export const getInitiativePage = (pathname) => {
  // Check for change-insights-analysis first (more specific)
  if (matchPath(ROUTES.CHANGE_INSIGHTS_ANALYSIS_SHOW, pathname)) {
    return 'change-insights-analysis';
  }

  // Check for change-insights
  if (matchPath(ROUTES.CHANGE_INSIGHTS_STUDIO_SHOW, pathname)) {
    return 'change-insights';
  }

  // Check for files
  if (matchPath(ROUTES.INITIATIVES_FILES, pathname)) {
    return 'files';
  }

  // Check if it's just the show page (no subpage)
  const showMatch = matchPath(ROUTES.INITIATIVES_SHOW, pathname);
  if (
    showMatch &&
    !matchPath('/:slug/:initiativeType/:initiativeId/*', pathname)
  ) {
    return 'show';
  }

  return null;
};

/**
 * Detect organization page type
 */
export const getOrganizationPage = (pathname) => {
  // Custom settings sub-pages
  if (matchPath(ROUTES.ORGANIZATION_CUSTOM_SETTINGS_STATUSES_EDIT, pathname)) {
    return 'custom-settings-statuses-edit';
  }
  if (
    matchPath(
      ROUTES.ORGANIZATION_CUSTOM_SETTINGS_CHANGE_IMPACT_TYPES_EDIT,
      pathname
    )
  ) {
    return 'custom-settings-change-impact-types-edit';
  }
  if (
    matchPath(
      ROUTES.ORGANIZATION_CUSTOM_SETTINGS_ORGANIZATION_SETTINGS_EDIT,
      pathname
    )
  ) {
    return 'custom-settings-organization-settings-edit';
  }
  if (
    matchPath(ROUTES.ORGANIZATION_CUSTOM_SETTINGS_CATEGORIES_EDIT, pathname)
  ) {
    return 'custom-settings-categories-edit';
  }
  if (
    matchPath(ROUTES.ORGANIZATION_CUSTOM_SETTINGS_CHANGE_INSIGHTS_NEW, pathname)
  ) {
    return 'custom-settings-change-insights-new';
  }
  if (
    matchPath(
      ROUTES.ORGANIZATION_CUSTOM_SETTINGS_CHANGE_INSIGHTS_EDIT,
      pathname
    )
  ) {
    return 'custom-settings-change-insights-edit';
  }
  if (
    matchPath(
      ROUTES.ORGANIZATION_CUSTOM_SETTINGS_CHANGE_INSIGHTS_PREVIEW_NEW,
      pathname
    ) ||
    matchPath(
      ROUTES.ORGANIZATION_CUSTOM_SETTINGS_CHANGE_INSIGHTS_PREVIEW_EDIT,
      pathname
    )
  ) {
    return 'custom-settings-change-insights-preview';
  }
  if (matchPath(ROUTES.ORGANIZATION_CUSTOM_SETTINGS, pathname)) {
    return 'custom-settings';
  }

  // Change insights studio
  if (matchPath(ROUTES.CHANGE_INSIGHTS_STUDIO_PREVIEW, pathname)) {
    return 'change-insights-preview';
  }

  // User permissions
  if (
    matchPath(ROUTES.ORGANIZATION_USERS_PERMISSIONS_NEW, pathname) ||
    matchPath(ROUTES.ORGANIZATION_USERS_PERMISSIONS_EDIT, pathname)
  ) {
    return 'user-permissions-edit';
  }
  if (matchPath(ROUTES.ORGANIZATION_USERS_PERMISSIONS_SHOW, pathname)) {
    return 'user-permissions';
  }

  // User management
  if (matchPath(ROUTES.ORGANIZATION_USERS_NEW, pathname)) {
    return 'users-new';
  }
  if (matchPath(ROUTES.ORGANIZATION_USERS_EDIT, pathname)) {
    return 'user-edit';
  }
  if (matchPath(ROUTES.ORGANIZATION_USERS_SHOW, pathname)) {
    return 'user-show';
  }
  if (matchPath(ROUTES.ORGANIZATION_USERS, pathname)) {
    return 'users';
  }

  // Employees
  if (
    matchPath(ROUTES.EMPLOYEES_SHOW, pathname) ||
    matchPath(ROUTES.EMPLOYEES_CHANGE_IMPACTS, pathname) ||
    matchPath(ROUTES.EMPLOYEES_TASKS, pathname) ||
    matchPath(ROUTES.EMPLOYEES_STAKEHOLDER_PROFILES, pathname)
  ) {
    return 'employee-show';
  }
  if (matchPath(ROUTES.ORGANIZATION_EMPLOYEES_EDIT, pathname)) {
    return 'employees-edit';
  }
  if (matchPath(ROUTES.ORGANIZATION_EMPLOYEES, pathname)) {
    return 'employees';
  }

  // Stakeholders
  if (matchPath(ROUTES.ORGANIZATION_STAKEHOLDERS_NEW, pathname)) {
    return 'stakeholders-edit';
  }
  if (matchPath(ROUTES.ORGANIZATION_STAKEHOLDERS, pathname)) {
    return 'stakeholders';
  }

  // Organization edit
  if (matchPath(ROUTES.ORGANIZATION_EDIT, pathname)) {
    return 'organization-edit';
  }

  // Organization show
  if (matchPath(ROUTES.ORGANIZATION_SHOW, pathname)) {
    return 'organization';
  }

  return null;
};

/**
 * Check if current route matches a pattern
 * By default does exact matching (end: true) to prevent partial matches
 */
export const matchesRoute = (pathname, pattern, exact = true) => {
  return matchPath({ path: pattern, end: exact }, pathname) !== null;
};
